import type { Schema } from '@dealdesk/api-schema';
import { queryOptions } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { STALE } from '@/shared/api/stale';

export type ProductCode = Schema<'ProductCode'>;

export const configKeys = {
  banks: ['banks'] as const,
  bank: (bankId: string) => ['banks', bankId] as const,
  all: ['config'] as const,
  versions: (bankId: string, product: ProductCode) => ['config', bankId, product, 'versions'] as const,
  version: (bankId: string, product: ProductCode, versionNo: number) => ['config', bankId, product, versionNo] as const,
  compare: (bankId: string, product: ProductCode, base: number, target: number) => ['config', bankId, product, 'compare', base, target] as const,
  stages: (bankId: string) => ['banks', bankId, 'stages'] as const,
  employerCategories: (bankId: string) => ['banks', bankId, 'employer-categories'] as const,
  schemes: (bankId: string, product: ProductCode) => ['banks', bankId, 'schemes', product] as const,
  institutions: ['reference', 'financial-institutions'] as const,
};

/** The bank master as reference institutions (B1): every bank and finance company, partner or not, with aliases. */
export const institutionsQuery = queryOptions({
  queryKey: configKeys.institutions,
  queryFn: async ({ signal }) => (await unwrap(api.GET('/reference/financial-institutions', { signal }))).items,
  staleTime: STALE.reference,
});

export const banksQuery = queryOptions({
  queryKey: configKeys.banks,
  queryFn: ({ signal }) => unwrap(api.GET('/banks', { signal })),
  staleTime: STALE.reference,
});

export const bankQuery = (bankId: string) =>
  queryOptions({
    queryKey: configKeys.bank(bankId),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}', { params: { path: { bankId } }, signal })),
    staleTime: STALE.reference,
  });

export const versionsQuery = (bankId: string, product: ProductCode) =>
  queryOptions({
    queryKey: configKeys.versions(bankId, product),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/products/{productCode}/versions', { params: { path: { bankId, productCode: product } }, signal })),
    staleTime: STALE.reference,
  });

export const configVersionQuery = (bankId: string, product: ProductCode, versionNo: number) =>
  queryOptions({
    queryKey: configKeys.version(bankId, product, versionNo),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/products/{productCode}/versions/{versionNo}', { params: { path: { bankId, productCode: product, versionNo } }, signal })),
    staleTime: STALE.reference,
  });

export const compareQuery = (bankId: string, product: ProductCode, base: number, target: number) =>
  queryOptions({
    queryKey: configKeys.compare(bankId, product, base, target),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/products/{productCode}/compare', { params: { path: { bankId, productCode: product }, query: { base, target } }, signal })),
    staleTime: STALE.reference,
  });

/** The bank's operational stage catalogue with its macro-stage and reporting mapping. */
export const stagesQuery = (bankId: string) =>
  queryOptions({
    queryKey: configKeys.stages(bankId),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/stages', { params: { path: { bankId } }, signal })),
    staleTime: STALE.reference,
  });

export const employerCategoriesQuery = (bankId: string) =>
  queryOptions({
    queryKey: configKeys.employerCategories(bankId),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/employer-categories', { params: { path: { bankId } }, signal })),
    staleTime: STALE.reference,
  });

export const schemesQuery = (bankId: string, product: ProductCode) =>
  queryOptions({
    queryKey: configKeys.schemes(bankId, product),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/products/{productCode}/schemes', { params: { path: { bankId, productCode: product } }, signal })),
    staleTime: STALE.reference,
  });
